import { useContext } from 'react';
import { SafeAreaInsetsContext } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { SheetMenu, type SheetMenuItem } from '../primitives/SheetMenu';
import { useTheme } from './theme';

// Bottom action-sheet for managing an EXISTING superset (inbox 963ca6f2, batch
// 2). Reachable from the ☰ on the group header in BOTH loggers. One discoverable
// home for: save-as-template, add a member, add or remove a round (#387: the
// card's ＋/− act on one exercise only), reorder (up/down on the shown member),
// remove a member, dissolve. (Rename removed AP-0n: the superset has
// no visible name any more, so there was nothing to rename.)
export function SupersetMenuSheet({
  visible,
  groupLabel,
  canMoveUp,
  canMoveDown,
  onSaveTemplate,
  onAddExercise,
  onAddRound,
  onRemoveRound,
  canRemoveRound = false,
  onMoveUp,
  onMoveDown,
  onRemoveMember,
  onSplit,
  onClose,
}: {
  visible: boolean;
  groupLabel: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** Absent ⇒ no row (PE-26); a row that is shown but cannot act (move, remove round) is disabled and says so. */
  onSaveTemplate?: () => void;
  onAddExercise: () => void;
  /** #387: one more set on every member. Absent ⇒ no row, as above. */
  onAddRound?: () => void;
  /** #387: the pending sets of the group's highest open round (shared
   *  pendingRoundIdx). Absent ⇒ no row; `canRemoveRound` false ⇒ the row is
   *  there but disabled (no member has a pending set to spare). */
  onRemoveRound?: () => void;
  canRemoveRound?: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemoveMember: () => void;
  onSplit: () => void;
  onClose: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  // The context, not the hook: no provider (a bare test mount) means no inset.
  const insets = useContext(SafeAreaInsetsContext);

  const items: SheetMenuItem[] = [
    ...(onSaveTemplate
      ? [{ key: 'template', icon: 'star' as const, fill: true, label: t('mobile.superset.menuSaveTemplate', { defaultValue: 'Save as template' }), onPress: onSaveTemplate }]
      : []),
    { key: 'add', icon: 'plus', label: t('mobile.superset.menuAddExercise', { defaultValue: 'Add exercise' }), onPress: onAddExercise },
    ...(onAddRound
      ? [{ key: 'addRound', icon: 'between-horizontal-start' as const, label: t('mobile.superset.menuAddRound', { defaultValue: 'Add round' }), onPress: onAddRound }]
      : []),
    ...(onRemoveRound
      ? [{ key: 'removeRound', icon: 'minus' as const, label: t('mobile.superset.menuRemoveRound', { defaultValue: 'Remove round' }), onPress: onRemoveRound, disabled: !canRemoveRound }]
      : []),
    { key: 'up', icon: 'chevron-up', label: t('mobile.superset.menuMoveUp', { defaultValue: 'Move up' }), onPress: onMoveUp, disabled: !canMoveUp },
    { key: 'down', icon: 'chevron-down', label: t('mobile.superset.menuMoveDown', { defaultValue: 'Move down' }), onPress: onMoveDown, disabled: !canMoveDown },
    { key: 'remove', icon: 'trash', label: t('mobile.superset.menuRemoveMember', { defaultValue: 'Remove this exercise' }), onPress: onRemoveMember },
    { key: 'split', icon: 'scissors', label: t('mobile.superset.menuSplit', { defaultValue: 'Dissolve superset' }), onPress: onSplit, destructive: true },
  ];

  return (
    <SheetMenu
      theme={theme}
      visible={visible}
      onClose={onClose}
      title={groupLabel}
      cancelLabel={t('mobile.logger.cancel', { defaultValue: 'Cancel' })}
      items={items}
      bottomInset={insets?.bottom ?? 0}
    />
  );
}
